import { cn, Text } from '@viapeople/components'
import type React from 'react'

interface PaginationProps {
	currentPage: number
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>
	itemsPerPage: number
	totalItems: number
}

export const Pagination: React.FC<PaginationProps> = ({ currentPage, setCurrentPage, itemsPerPage, totalItems }) => {
	const totalPages = Math.ceil(totalItems / itemsPerPage)
	const indexOfLastItem = currentPage * itemsPerPage
	const indexOfFirstItem = indexOfLastItem - itemsPerPage

	const handlePreviousPage = () => {
		setCurrentPage((prev) => Math.max(prev - 1, 1))
	}

	const handleNextPage = () => {
		setCurrentPage((prev) => Math.min(prev + 1, totalPages))
	}

	const getPageNumbers = () => {
		const pageNumbers = []

		let startPage = Math.max(1, currentPage - 2)
		const endPage = Math.min(totalPages, startPage + 4)

		if (endPage - startPage < 4 && startPage > 1) {
			startPage = Math.max(1, endPage - 4)
		}

		for (let i = startPage; i <= endPage; i++) {
			pageNumbers.push(i)
		}

		return pageNumbers
	}

	const pageNumbers = getPageNumbers()

	return (
		<div className='flex justify-between items-center px-6 py-3 border-t border-neutral-200'>
			<div>
				<Text variant='xsmall'>
					<span className='text-neutral-400'>Showing</span> {indexOfFirstItem + 1}{' '}
					<span className='text-neutral-400'>to</span> {Math.min(indexOfLastItem, totalItems)}{' '}
					<span className='text-neutral-400'>of</span> {totalItems} <span className='text-neutral-400'>results</span>
				</Text>
			</div>
			<div className='flex items-center gap-3'>
				<button
					type='button'
					onClick={handlePreviousPage}
					disabled={currentPage === 1}
					className={`px-2 ${currentPage === 1 ? 'text-neutral-300 cursor-not-allowed' : 'text-neutral-600 cursor-pointer'}`}>
					&lt;
				</button>

				{pageNumbers.map((pageNumber) => (
					<button
						type='button'
						key={pageNumber}
						onClick={() => setCurrentPage(pageNumber)}
						className={cn(
							'w-8 h-8 flex items-center justify-center text-sm text-neutral-400 cursor-pointer',
							pageNumber === currentPage
								? 'bg-neutral-100 text-neutral-800 rounded-sm font-medium'
								: 'hover:bg-neutral-50',
						)}>
						{pageNumber}
					</button>
				))}

				<button
					type='button'
					onClick={handleNextPage}
					disabled={currentPage === totalPages}
					className={`px-2 ${currentPage === totalPages ? 'text-neutral-300 cursor-not-allowed' : 'text-neutral-600 cursor-pointer'}`}>
					&gt;
				</button>
			</div>
		</div>
	)
}
